import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {KeyValue} from '../../components/common/arr-transfer-list/ArrTransferList';
import {ToastType} from '../../shared/constants';
import {ISelectItem} from '../../shared/types';

export interface ToggleItem {
  key: string;
  label: string;
  checked: boolean;
  disabled?: boolean;
  parent?: string;
}

interface SelectedProject {
  key: string;
  displayValue: string;
}

export interface ProjectFormState {
  projectId: number;
  encryptedProjectId: string;
  type?: string;
  projectName: string;
  alphaCode: string;
  selectedClient: string;
  selectedClientId: number | null;
  initialAlphaCode: string;
  isActiveProject: boolean;
  deleteProject: boolean;
  selectedProject: SelectedProject | null;
  projectFolderPath: string;
  ingestionFolderPath: string;
  productionFolderPath: string;

  projectServerSelected: KeyValue[];
  loadServerSelected: KeyValue[];
  productionServerSelected: KeyValue[];

  toggles: ToggleItem[];

  isAnalyticsActive: boolean;
  isRedactionPropagationActive: boolean;
  analyticsComponentsSelected: KeyValue[];
  analyticsAutomationSelected: KeyValue[];

  prefix: string;
  suffix: string;
  minLength: string;
  analyticsEngine: {label: string; value: number} | null;
  projectGuid: string;
  projectIndexGuid: string;
  formDirty: boolean;
  projectDBServerId: number;
  redactionPropagationDisable?: boolean;
  showToast: boolean;
  showToastType: ToastType;
  showToastMessage?: string;
  analyticsEngineList?: ISelectItem[];
  isGenerativeAIActive: boolean;
  generativeAISelected: KeyValue[];
}

const initialState: ProjectFormState = {
  type: '',
  projectId: 0,
  encryptedProjectId: '',
  projectName: '',
  alphaCode: '',
  selectedClient: '',
  selectedClientId: null,
  initialAlphaCode: '',
  isActiveProject: true,
  deleteProject: false,
  selectedProject: null,
  projectFolderPath: '',
  ingestionFolderPath: '',
  productionFolderPath: '',
  projectServerSelected: [],
  loadServerSelected: [],
  productionServerSelected: [],

  toggles: [],

  isAnalyticsActive: true,
  isRedactionPropagationActive: true,
  analyticsComponentsSelected: [],
  analyticsAutomationSelected: [],

  prefix: 'ID',
  suffix: '',
  minLength: '8',
  analyticsEngine: null,
  projectGuid: '',
  projectIndexGuid: '',
  formDirty: false,
  projectDBServerId: 0,
  redactionPropagationDisable: false,
  showToast: false,
  showToastType: ToastType.Success,
  showToastMessage: '',
  analyticsEngineList: [],
  isGenerativeAIActive: false,
  generativeAISelected: [],
};

const projectFormSlice = createSlice({
  name: 'projectForm',
  initialState,
  reducers: {
    setProjectName(state, action: PayloadAction<string>) {
      state.projectName = action.payload;
    },
    setAlphaCode(state, action: PayloadAction<string>) {
      state.alphaCode = action.payload;
    },
    setSelectedClient(state, action: PayloadAction<string>) {
      state.selectedClient = action.payload;
    },
    setSelectedClientId(state, action: PayloadAction<number | null>) {
      state.selectedClientId = action.payload;
    },

    setIsActiveProject(state, action: PayloadAction<boolean>) {
      state.isActiveProject = action.payload;
    },
    setDeleteProject(state, action: PayloadAction<boolean>) {
      state.deleteProject = action.payload;
    },
    setSelectedProject(state, action: PayloadAction<SelectedProject | null>) {
      state.selectedProject = action.payload;
    },
    setServerSelected(
      state,
      action: PayloadAction<{
        type: 'project' | 'load' | 'production';
        selected: KeyValue[];
      }>
    ) {
      const {type, selected} = action.payload;
      if (type === 'project') state.projectServerSelected = [...selected];
      if (type === 'load') state.loadServerSelected = [...selected];
      if (type === 'production') state.productionServerSelected = [...selected];
    },
    setFolderPaths(
      state,
      action: PayloadAction<{
        type: 'project' | 'ingestion' | 'production';
        path: string;
      }>
    ) {
      const {type, path} = action.payload;
      if (type === 'project') state.projectFolderPath = path;
      if (type === 'ingestion') state.ingestionFolderPath = path;
      if (type === 'production') state.productionFolderPath = path;
    },

    setToggles(state, action: PayloadAction<ToggleItem[]>) {
      state.toggles = action.payload;
    },

    setFormDirty(state, action: PayloadAction<boolean>) {
      state.formDirty = action.payload;
    },

    updateToggleChecked(
      state,
      action: PayloadAction<{key: string; checked: boolean}>
    ) {
      const {key, checked} = action.payload;
      state.toggles = state.toggles.map((item) =>
        item.key === key ? {...item, checked} : item
      );
    },

    setIsAnalyticsActive(state, action: PayloadAction<boolean>) {
      state.isAnalyticsActive = action.payload;
    },
    setIsRedactionPropagationActive(state, action: PayloadAction<boolean>) {
      state.isRedactionPropagationActive = action.payload;
    },
    setAnalyticsComponentsSelected(state, action: PayloadAction<KeyValue[]>) {
      state.analyticsComponentsSelected = action.payload;
    },
    setAnalyticsAutomationSelected(state, action: PayloadAction<KeyValue[]>) {
      state.analyticsAutomationSelected = action.payload;
    },
    setGenerativeAISelected(state, action: PayloadAction<KeyValue[]>) {
      state.generativeAISelected = action.payload;
    },

    setPrefix(state, action: PayloadAction<string>) {
      state.prefix = action.payload;
    },
    setSuffix(state, action: PayloadAction<string>) {
      state.suffix = action.payload;
    },
    setMinLength(state, action: PayloadAction<string>) {
      state.minLength = action.payload;
    },
    setAnalyticsEngine(
      state,
      action: PayloadAction<ProjectFormState['analyticsEngine']>
    ) {
      state.analyticsEngine = action.payload;
    },
    setProjectId(state, action: PayloadAction<string>) {
      state.encryptedProjectId = action.payload;
    },
    setType(state, action: PayloadAction<string>) {
      state.type = action.payload;
    },
    setProjectDBServerId(state, action: PayloadAction<number>) {
      state.projectDBServerId = action.payload;
    },
    setRedactionPropagationDisable(state, action: PayloadAction<boolean>) {
      state.redactionPropagationDisable = action.payload;
    },
    setShowToastSave(state, action: PayloadAction<boolean>) {
      state.showToast = action.payload;
    },
    setShowType(state, action: PayloadAction<ToastType>) {
      state.showToastType = action.payload;
    },
    setShowMessage(state, action: PayloadAction<string>) {
      state.showToastMessage = action.payload;
    },
    setIsGenerativeAIActive(state, action: PayloadAction<boolean>) {
      state.isGenerativeAIActive = action.payload;
    },

    setEditValues(state, action: PayloadAction<any>) {
      state.projectName = action.payload.projectLabel;
      state.alphaCode = action.payload.hcode;
      state.initialAlphaCode = action.payload.hcode;
      state.selectedClientId = action.payload.clientEntityID;
      state.isActiveProject = action.payload.isActive;
      state.projectFolderPath = action.payload.projectDetails.projectFolder;
      state.ingestionFolderPath = action.payload.projectDetails.ingestionFolder;
      state.productionFolderPath =
        action.payload.projectDetails.productionFolder;
      state.isRedactionPropagationActive =
        action.payload.projectSettings.isRedactionPropagated;
      state.isAnalyticsActive =
        action.payload.projectSettings.isEnabledAnalytics;
      state.prefix = action.payload.projectSettings.docIDPrefix;
      state.suffix = action.payload.projectSettings.docIDSuffix;
      state.minLength = action.payload.projectSettings.docIDMinLengthNo;
      state.projectId = action.payload.projectId;
      state.projectGuid = action.payload.projectGUID;
      state.projectIndexGuid = action.payload.projectIndexGUID;
      state.analyticsEngineList = action.payload.analyticsEngine;
    },

    resetProjectForm: () => initialState,
  },
});

export const {
  setProjectName,
  setAlphaCode,
  setSelectedClient,
  setSelectedClientId,
  setIsActiveProject,
  setDeleteProject,
  setSelectedProject,
  setFolderPaths,
  setServerSelected,
  setToggles,
  updateToggleChecked,
  setIsAnalyticsActive,
  setIsRedactionPropagationActive,
  setAnalyticsComponentsSelected,
  setAnalyticsAutomationSelected,
  setPrefix,
  setSuffix,
  setMinLength,
  setAnalyticsEngine,
  resetProjectForm,
  setProjectId,
  setType,
  setEditValues,
  setFormDirty,
  setProjectDBServerId,
  setRedactionPropagationDisable,
  setShowMessage,
  setShowToastSave,
  setShowType,
  setIsGenerativeAIActive,
  setGenerativeAISelected,
} = projectFormSlice.actions;

export default projectFormSlice.reducer;
